import React, { FC, useCallback, useEffect, useState } from 'react';
import { Collapse } from 'bootstrap';
import { RoundIcon, RoundIconVariants } from '@components/atoms/RoundIcon';
import { Container } from '@components/atoms/Container';
import { Anchor, AnchorMode } from '@components/atoms/Anchor';
import { SideMenu } from '@components/molecules/SideMenu';
import { LanguagesDropdown } from './LanguagesDropdown';
import { UserProfileDropdown } from './UserProfileDropdown';
import styles from './Header.module.css';
import { useThemes } from '@hooks/useThemes';
import { useData } from '@hooks/useData';
import { SITE_URLS } from '@constants';
import { Modules, useModule } from '@hooks/useModule';
import logoSite from '@assets/images/logos/logo-site.svg';
import logoSiteWhite from '@assets/images/logos/logo-site-white.svg';
import { useGetUser } from '@services/useGetUser';

export interface HeaderProps {
    mode?: 'with-separator' | 'logo-only';
    variant?: 'default' | 'transparent';
}

export const Header: FC<HeaderProps> = ({ mode, variant }) => {
    const { isModuleActive } = useModule();
    const { allSettings, login, user } = useData();
    const { useGrayscaleTheme, toggleGrayscaleTheme, increaseFontSize, decreaseFontSize } = useThemes();
    const { request: requestUser } = useGetUser();
    const [isScrolled, setIsScrolled] = useState(false);
    const [sidebar, setSidebar] = useState<Collapse | null>(null);

    const inCatalogMode = isModuleActive(Modules.catalogMode);

    useEffect(() => {
        requestUser();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (variant !== 'transparent') {
            setIsScrolled(false);
            return;
        }

        const handleScroll = () => {
            setIsScrolled(window.scrollY > 0);
        };

        handleScroll();

        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [variant]);

    useEffect(() => {
        if (user?.data?.photo && login?.data?.user) {
            login.data.user.photo = user.data.photo;
        }
    }, [user, login]);

    useEffect(() => {
        return () => {
            if (sidebar) {
                try {
                    const element = document.querySelector('[data-testid="collapseSideMenu"]');
                    if (element) {
                        sidebar.dispose();
                    }
                } catch (e) {
                    // evita quebrar desmontagem caso o Collapse ja tenha sido destruido
                }
            }
        };
    }, [sidebar]);

    const sidebarRef = useCallback((node: HTMLDivElement | null) => {
        if (node !== null) {
            const collapseInstance = Collapse.getOrCreateInstance(node, { toggle: false });
            setSidebar(collapseInstance);
        }
    }, []);

    const openSidebar = () => {
        sidebar?.show();
    };

    const closeSidebar = () => {
        try {
            sidebar?.hide();
        } catch (e) {
            // console.log('catch error closing sidebar');
        }
    };

    const logoItem = allSettings?.data?.layouts?.find((item) => item.name === 'logo');

    const isTransparentVariant = variant === 'transparent';
    const isTransparentHeaderActive = isTransparentVariant && !isScrolled;

    const defaultLogoSrc = logoItem?.value || logoSite;
    const logoSrc = isTransparentHeaderActive ? logoSiteWhite : defaultLogoSrc;

    return (
        <>
            <header
                className={
                    // themes
                    `${useGrayscaleTheme ? styles['theme-grayscale'] : ''}` +
                    // base variant (transparente ou padrão)
                    ` ${isTransparentHeaderActive ? styles.headerTransparent : styles.header}` +
                    // fixo no topo apenas quando usamos a variante transparente (Home)
                    `${isTransparentVariant ? ` ${styles.headerSticky}` : ''}` +
                    ' py-3'
                }>
                <Container>
                    <>
                        <div
                            className={`d-flex flex-wrap align-items-center justify-content-center justify-content-lg-start ${styles['header-content']}`}>
                            <Anchor
                                href={SITE_URLS.home}
                                classes="d-flex align-items-center link-body-emphasis text-decoration-none"
                                mode={AnchorMode.none}>
                                <img className={styles['brand-logo']} src={logoSrc} alt="" />
                            </Anchor>
                            <div className="nav ms-auto d-flex align-items-center">
                                {(!mode || mode !== 'logo-only') && (
                                    <>
                                        {/* decrease font size */}
                                        <div className="me-3 align-self-center d-none d-md-block">
                                            <RoundIcon
                                                variant={RoundIconVariants.variant1}
                                                text="A–"
                                                onClick={() => decreaseFontSize()}
                                            />
                                        </div>

                                        {/* increase font size */}
                                        <div className="me-3 align-self-center d-none d-md-block">
                                            <RoundIcon
                                                variant={RoundIconVariants.variant1}
                                                text="A+"
                                                onClick={() => increaseFontSize()}
                                            />
                                        </div>

                                        {/* grayscale theme */}
                                        <div className="me-3 me-sm-4 align-self-center d-none d-md-block">
                                            <RoundIcon
                                                variant={RoundIconVariants.variant1}
                                                active={useGrayscaleTheme}
                                                icon={`${useGrayscaleTheme ? 'fa-solid' : 'fa-light'} fa-browser`}
                                                onClick={() => toggleGrayscaleTheme()}
                                            />
                                        </div>

                                        {/* language dropdown */}
                                        <div className="me-3 me-sm-4 align-self-center">
                                            <LanguagesDropdown />
                                        </div>

                                        {/* user dropdown */}
                                        <div className={!inCatalogMode ? 'me-3 me-sm-4' : ''}>
                                            <UserProfileDropdown user={user?.data ?? undefined} />
                                        </div>

                                        {!inCatalogMode && (
                                            <div className="d-flex align-items-center">
                                                <Anchor href="shopping-cart" mode={AnchorMode.none}
                                                        classes="text-decoration-none">
                                                    <RoundIcon
                                                        variant={RoundIconVariants.variant1}
                                                        icon="fa-light fa-basket-shopping"
                                                        indicator={undefined}
                                                    />
                                                </Anchor>
                                            </div>
                                        )}
                                    </>
                                )}

                                <button
                                    type="button"
                                    className={styles['menu-toggle']}
                                    onClick={openSidebar}
                                    aria-label="Abrir menu">
                                    <i className="fa-light fa-bars" />
                                </button>
                            </div>
                        </div>
                        {mode === 'with-separator' && <div className={styles['header-separator']}></div>}
                    </>
                </Container>
            </header>
            <SideMenu menuRef={sidebarRef} onClose={closeSidebar} />
        </>
    );
};

